'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Heart, Droplets, Flower2, Shield, Briefcase, ShieldCheck, ArrowLeft, CheckCircle2, ChevronRight, Phone, AlertCircle } from 'lucide-react';
import TopNav from '@/components/layout/TopNav';
import { checkupCategories, getPackageByCategory, type CheckupCategory } from '@/data/healthCheckup';
import { trackEvent } from '@/lib/analytics';
import { hospitalConfig } from '@/data/hospitalConfig';
import { whatsAppMessages } from '@/data/hospitalConfig';
import WhatsAppButton from '@/components/ui/WhatsAppButton';
import Icon from '@/components/ui/AppIcon';
import { useSelectedCentre } from '@/context/SelectedCentreContext';
import { MapPin } from 'lucide-react';


type CheckupStep = 'select' | 'package' | 'contact';

const iconMap: Record<string, React.ElementType> = {
  Activity, Heart, Droplets, Flower2, Shield, Briefcase, ShieldCheck,
};

const colorMap: Record<string, { border: string; bg: string; text: string; glow: string }> = {
  emerald: { border: 'border-emerald-500/40', bg: 'bg-emerald-500/10', text: 'text-emerald-400', glow: 'shadow-emerald-500/20' },
  rose: { border: 'border-rose-500/40', bg: 'bg-rose-500/10', text: 'text-rose-400', glow: 'shadow-rose-500/20' },
  amber: { border: 'border-amber-500/40', bg: 'bg-amber-500/10', text: 'text-amber-400', glow: 'shadow-amber-500/20' },
  pink: { border: 'border-pink-500/40', bg: 'bg-pink-500/10', text: 'text-pink-400', glow: 'shadow-pink-500/20' },
  blue: { border: 'border-blue-500/40', bg: 'bg-blue-500/10', text: 'text-blue-400', glow: 'shadow-blue-500/20' },
  gold: { border: 'border-yellow-500/40', bg: 'bg-yellow-500/10', text: 'text-yellow-400', glow: 'shadow-yellow-500/20' },
  teal: { border: 'border-teal-500/40', bg: 'bg-teal-500/10', text: 'text-teal-400', glow: 'shadow-teal-500/20' },
};

const slideVariants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

export default function HealthCheckupContent() {
  const router = useRouter();
  const { selectedCentre } = useSelectedCentre();
  const [step, setStep] = useState<CheckupStep>('select');
  const [selectedCategory, setSelectedCategory] = useState<CheckupCategory | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    trackEvent({ event: 'health_checkup_started', source: 'health-checkup-page' });
    return () => clearTimeout(t);
  }, []);

  const handleCategorySelect = (cat: CheckupCategory) => {
    setSelectedCategory(cat);
    trackEvent({ event: 'concern_selected', source: cat.id });
    setStep('package');
  };

  const handleBack = () => {
    if (step === 'package') setStep('select');
    else if (step === 'contact') setStep('package');
    else router.push('/digital-reception-screen');
  };

  const pkg = selectedCategory ? getPackageByCategory(selectedCategory.id) : null;

  return (
    <main
      className="checkup-bg min-h-screen flex flex-col pb-24 md:pb-12 overflow-x-hidden"
      role="main"
      aria-label="Find the Right Health Check-Up"
    >
      <TopNav showBackButton backHref="/digital-reception-screen" backLabel="Reception" />

      <div className="flex-1 flex flex-col items-center justify-start pt-24 px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 20 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-3xl"
        >
          {/* Header */}
          <div className="mb-8">
            {step !== 'select' && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm font-medium mb-6 focus-gold rounded"
                aria-label="Go back"
              >
                <ArrowLeft size={16} />
                <span>BACK</span>
              </button>
            )}
            <p className="section-label mb-3">HEALTH CHECK-UP</p>
            {selectedCentre && (
              <div className="flex items-center gap-2 mb-3 text-xs text-primary/70">
                <MapPin size={11} />
                <span className="font-semibold tracking-wide">{selectedCentre.shortName}</span>
              </div>
            )}
            <AnimatePresence mode="wait">
              {step === 'select' && (
                <motion.div key="title-select" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <h1 className="text-display-md text-white font-bold mb-2 leading-tight">
                    FIND THE RIGHT<br />
                    <span className="gold-gradient-text">HEALTH CHECK-UP</span>
                  </h1>
                  <p className="text-muted-foreground text-sm mt-3">
                    WHAT WOULD YOU LIKE TO CHECK?
                  </p>
                </motion.div>
              )}
              {step === 'package' && selectedCategory && (
                <motion.div key="title-package" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <h1 className="text-display-md text-white font-bold mb-2 leading-tight">
                    YOUR RECOMMENDED<br />
                    <span className="gold-gradient-text">CHECK-UP</span>
                  </h1>
                  <p className="text-muted-foreground text-sm mt-3">
                    Based on your selection: <span className="text-primary">{selectedCategory.label}</span>
                  </p>
                </motion.div>
              )}
              {step === 'contact' && (
                <motion.div key="title-contact" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <h1 className="text-display-md text-white font-bold mb-2 leading-tight">
                    BOOK YOUR<br />
                    <span className="gold-gradient-text">CHECK-UP</span>
                  </h1>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            {/* STEP: SELECT CATEGORY */}
            {step === 'select' && (
              <motion.div
                key="step-select"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: 'easeOut' }}
              >
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                  role="radiogroup"
                  aria-label="Select health check-up category"
                >
                  {checkupCategories.map((cat, idx) => {
                    const Icon = iconMap[cat.iconType] ?? Activity;
                    const colors = colorMap[cat.color] ?? colorMap.teal;
                    return (
                      <motion.button
                        key={cat.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.06, duration: 0.4 }}
                        onClick={() => handleCategorySelect(cat)}
                        className={`checkup-category-card group ${colors.border} hover:${colors.glow}`}
                        role="radio"
                        aria-checked={false}
                        aria-label={cat.label}
                      >
                        <div className={`checkup-icon-wrap ${colors.bg} ${colors.border} group-hover:scale-110 transition-transform duration-300`}>
                          <Icon size={24} className={colors.text} aria-hidden="true" />
                        </div>
                        <div className="flex-1 text-left">
                          <span className="block font-bold text-sm tracking-widest text-foreground uppercase leading-snug">
                            {cat.label}
                          </span>
                          <span className="block text-xs text-muted-foreground mt-1">{cat.sublabel}</span>
                        </div>
                        <ChevronRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" aria-hidden="true" />
                      </motion.button>
                    );
                  })}
                </div>

                {/* Prototype notice */}
                <div className="mt-8 flex items-start gap-3 glass-card rounded-xl p-4">
                  <AlertCircle size={16} className="text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    <span className="text-primary font-semibold">PROTOTYPE DATA</span> — Package details shown are structured prototypes.
                    Verified All Is Well Health Check-Up packages will be connected when available.
                    Please contact the hospital directly for current package information.
                  </p>
                </div>
              </motion.div>
            )}

            {/* STEP: PACKAGE DETAIL */}
            {step === 'package' && selectedCategory && pkg && (
              <motion.div
                key="step-package"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: 'easeOut' }}
              >
                {(() => {
                  const Icon = iconMap[selectedCategory.iconType] ?? Activity;
                  const colors = colorMap[selectedCategory.color] ?? colorMap.teal;
                  return (
                    <div className="space-y-6">
                      {/* Package card */}
                      <div className={`glass-card rounded-2xl p-6 border ${colors.border}`}>
                        <div className="flex items-start gap-4 mb-6">
                          <div className={`checkup-icon-wrap-lg ${colors.bg} ${colors.border}`}>
                            <Icon size={32} className={colors.text} aria-hidden="true" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold text-white mb-1">{pkg.name}</h2>
                            <p className="text-sm text-muted-foreground">{pkg.tagline}</p>
                            <p className={`text-xs font-semibold mt-2 ${colors.text}`}>{selectedCategory.description}</p>
                          </div>
                        </div>

                        <div className="mb-5">
                          <p className="text-xs font-semibold text-muted-foreground tracking-widest uppercase mb-3">TYPICALLY INCLUDES</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {pkg.includes.map((item, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <CheckCircle2 size={14} className={`${colors.text} flex-shrink-0`} aria-hidden="true" />
                                <span className="text-sm text-foreground/80">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="border-t border-border/50 pt-4">
                          <p className="text-xs text-muted-foreground">
                            <span className="font-semibold text-foreground/70">SUITABLE FOR:</span> {pkg.suitableFor}
                          </p>
                        </div>
                      </div>

                      {/* Prototype notice */}
                      <div className="flex items-start gap-3 glass-card rounded-xl p-4">
                        <AlertCircle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          <span className="text-amber-400 font-semibold">PROTOTYPE:</span> {pkg.note}
                        </p>
                      </div>

                      {/* CTA */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => setStep('contact')}
                          className="btn-primary flex-1 text-sm tracking-widest"
                          aria-label="Book this health check-up"
                        >
                          BOOK THIS CHECK-UP →
                        </button>
                        <button
                          onClick={() => setStep('select')}
                          className="btn-outline-gold flex-1 text-sm tracking-widest"
                          aria-label="Choose a different check-up"
                        >
                          CHOOSE DIFFERENT
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            )}

            {/* STEP: CONTACT */}
            {step === 'contact' && (
              <motion.div
                key="step-contact"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: 'easeOut' }}
              >
                <div className="space-y-4">
                  <div className="glass-card rounded-2xl p-6">
                    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                      To book your health check-up, please contact All Is Well Hospital directly.
                      Our team will guide you through available packages, pricing, and scheduling.
                    </p>

                    <div className="flex flex-col gap-3">
                      <a
                        href={hospitalConfig.phoneTel}
                        onClick={() => trackEvent({ event: 'call_clicked', source: 'health-checkup' })}
                        className="btn-primary w-full text-sm tracking-widest flex items-center justify-center gap-3"
                        aria-label={`Call All Is Well Hospital: ${hospitalConfig.phone}`}
                      >
                        <Phone size={18} aria-hidden="true" />
                        CALL TO BOOK — {hospitalConfig.phone}
                      </a>
                      <WhatsAppButton
                        message={whatsAppMessages.healthCheckup(selectedCentre?.shortName)}
                        label="WHATSAPP US"
                        variant="inline"
                        className="w-full py-3 text-sm"
                        source="health-checkup-contact"
                      />
                    </div>
                  </div>

                  <div className="glass-card rounded-xl p-4">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      This is a care navigation guide only. Package availability, inclusions, and pricing
                      are subject to confirmation by All Is Well Hospital. Always consult a qualified
                      medical professional for health concerns.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </main>
  );
}
